/* eslint semi: 1 */
import { computed } from 'vue';
import { useLocalStorageStore } from '@base-shared/stores/local-storage';
import type { FileUploadItem } from '@base-shared/types/file-uploader';

/**
 * Composable that owns the reactive layer for in-flight file uploads.
 *
 * Raw localStorage access is delegated to {@link useLocalStorageStore};
 * this composable adds:
 *  - A reactive `files` ref that stays in sync after every write
 *  - Higher-level CRUD operations (upsert, remove, cleanup) built on
 *    the store's primitive read/write methods
 *
 * NOT responsible for:
 *  - Vuex broadcasting after mutations (callers dispatch their own events)
 *  - Upload API calls or business logic
 */
export function useFileUploadStorage () {
  const localStorageStore = useLocalStorageStore();

  const files = computed<FileUploadItem[]>(() => localStorageStore.aryaFileUploads);

  function getFiles (): FileUploadItem[] {
    return localStorageStore.getAryaFileUploadsFromStorage() as FileUploadItem[];
  }

  function setFiles (items: FileUploadItem[]): void {
    localStorageStore.setAryaFileUploadsToStorage(items);
  }

  /**
   * Adds a file if it doesn't exist yet, or updates it in-place by uid.
   */
  function upsertFile (fileItem: FileUploadItem): void {
    const current = getFiles();
    const idx = current.findIndex(f => f.uid === fileItem.uid);
    if (idx >= 0) {
      current[idx] = fileItem;
    } else {
      current.push(fileItem);
    }
    setFiles(current);
  }

  /**
   * Removes a file by resourceId (preferred) or by uid.
   * Callers are responsible for dispatching any Vuex events after removal.
   */
  function removeFile (file: Pick<FileUploadItem, 'uid' | 'resourceId'>): void {
    let current = getFiles();
    if (file.resourceId) {
      current = current.filter(item => item._edit != null && item._edit.resourceId !== file.resourceId);
    } else {
      current = current.filter(item => item.uid !== file.uid);
    }
    setFiles(current);
  }

  /**
   * Removes all upload entries (used on sign-out).
   */
  function clearAll (): void {
    localStorageStore.clearAryaFileUploadsFromStorage();
  }

  /**
   * On page mount, removes stale in-flight entries: keeps only files that
   * reached S3 success but haven't finished backend conversion yet,
   * so the component can resume watching them.
   */
  function cleanupOnMount (): void {
    const surviving = getFiles().filter(
      file =>
        file._edit != null &&
        !file._edit.uploadRequests.conversionFinished &&
        file._edit.uploadRequests.submitToS3Status === 'success'
    );
    setFiles(surviving);
  }

  return {
    /** Reactive snapshot of all in-flight uploads persisted in localStorage. */
    files,
    getFiles,
    setFiles,
    upsertFile,
    removeFile,
    clearAll,
    cleanupOnMount
  };
}
